import { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { SOCKET_URL } from '../config/config';
import { debounce } from 'lodash';

let socketInstance;

const useSocket = () => {
  const socketRef = useRef(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [data, setData] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [botChatUserList,setBotChatUserList] = useState({ userId: null, list: [] })

  useEffect(() => {
    if (!socketInstance) {
      const token = localStorage.getItem('authToken');
      // socketInstance = io(SOCKET_URL, {
      //   auth: { token: token },
      // });
      // API.replace("ws://", "wss://")
      socketInstance = io(SOCKET_URL, {
        auth: { token  },
        transports: ["websocket"],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,
      })

      // Bound to the socket's lifetime, not to a consumer's. There are nine useSocket()
      // call sites; announcing the admin from each of them on every reconnect would
      // multiply a server-facing notification that is meant to fire once.
      socketInstance.on('connect', () => {
        socketInstance.emit('adminConnect');
      });
    }

    socketRef.current = socketInstance;

    // Registered per mount, deliberately OUTSIDE the creation guard above. socketInstance
    // is module-level and outlives every component, so the old code bound these handlers
    // once — to whichever hook instance happened to construct the socket — and closed
    // over that instance's setters forever. Every other consumer, and every remount of
    // the same one, silently got no listener and permanently empty state.
    //
    // That was reachable in normal use: App.js mounts a useSocket at the app root, so
    // landing on any other page before Chat meant ChatApp's instance never received
    // recentChatListResponse. The "Unread Messages" filter emitted, the server replied,
    // and the response updated a dead instance — no error, no spinner, just nothing.
    const onConnect = () => setIsSocketConnected(true);
    const onDisconnect = () => setIsSocketConnected(false);
    const onMessage = (message) => setData(message);
    const onRecentChatList = (data) => setUsersList(data);
    // Keep the owner alongside the rows. The server debounces this reply, so a list
    // requested for bot A can arrive after the operator has already selected bot B;
    // applying it blind replaced B's inbox column with A's rows, and opening one of
    // those bound the message pane to A's room while it kept aligning against B's id
    // (ADMIN-18). Consumers compare `userId` before using `list`.
    const onRecentChatListResponse = (data) =>
      setBotChatUserList({ userId: data?.userId ?? null, list: data?.list || [] });

    socketInstance.on('connect', onConnect);
    socketInstance.on('disconnect', onDisconnect);
    socketInstance.on('message', onMessage);
    socketInstance.on('getRecentChatList', onRecentChatList);
    socketInstance.on('recentChatListResponse', onRecentChatListResponse);

    // A later mount attaches after 'connect' has already fired and would otherwise sit
    // at false until the next reconnect, so seed the flag from the live socket.
    setIsSocketConnected(socketInstance.connected);

    return () => {
      socketInstance.off('connect', onConnect);
      socketInstance.off('disconnect', onDisconnect);
      socketInstance.off('message', onMessage);
      socketInstance.off('getRecentChatList', onRecentChatList);
      socketInstance.off('recentChatListResponse', onRecentChatListResponse);
    };
  }, []);

  const sendMessage = (message) => {
    if (socketRef.current) {
      socketRef.current.emit('message', message);
    }
  };

  const notifyAdminConnection = () => {
    if (socketRef.current) {
      socketRef.current.emit('adminConnect');
    }
  };

  const debouncedTypingRef = useRef(
    debounce((userId, selectedUser) => {
      if (socketRef.current) {
        socketRef.current.emit('typing', {
          senderId: userId,
          receiverId: selectedUser?._id,
        });
      }
    }, 1000)
  );

  const handleTyping = (userId, selectedUser) => {
    debouncedTypingRef.current(userId, selectedUser);
  };

  const handleUserOnline = (userId) => {
    if (socketRef.current) {
      socketRef.current.emit('userOnline', { userId: userId,isAdmin:true });
    }
  };

  const handleUserOffline = (userId) => {
    if (socketRef.current) {
      socketRef.current.emit('userOffline', { userId: userId });
      socketRef.current.emit('admin-bot-offline', { userId: userId,isAdmin:true })
    }
  };

    const emitChatFilter = (data) => {
    if (socketRef.current) {
      socketRef.current.emit('getRecentChatList', data);
    }
  };

  // Notify an iOS user in real time when the admin approves/rejects their verification
  const emitVerificationStatus = (data) => {
    if (socketRef.current) {
      socketRef.current.emit('verificationStatusUpdate', data);
    }
  };



  return {
    socket: socketRef.current,
    isSocketConnected,
    sendMessage,
    notifyAdminConnection,
    handleTyping,
    handleUserOnline,
    handleUserOffline,
    emitChatFilter,
    emitVerificationStatus,
    botChatUserList
  };
};

export default useSocket;
